import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { js2xml } from 'xml-js';

import { Button, Tooltip } from '@components/common';
import Actions from '@components/common/Actions/Actions';
import AdaptiveTableCell from '@components/common/AdaptiveTableCell';

import { formatDate } from '@utils/date';
import { createNestedObjectFromFlatArray, getLastChild } from '@components/common/FlatXMLTree/utils';

import { IAvailableTimestamps } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	INetworkElementConfiguration,
} from '@interfaces/networkElementConfigurations/networkElementConfigurations';
import { IFlatXMLTreeNode, ITagStack, TMapTree } from '@components/common/FlatXMLTree/interface';

import { FLAT_NODE_PATH_SEPARATOR } from '@constants/common';
import { columnSizes } from '@constants/table';
import { NON_EDITABLE_CONFIGURATION_IDS } from './constants';

import tableStyles from '@components/common/Table/Table.module.scss';

export const parseRawConfig = (
	rawData: string,
	worker: Worker,
	setIsLoading?: (value: boolean) => void,
): Promise<TMapTree> => {
	if (setIsLoading) setIsLoading(true);

	return new Promise((resolve) => {
		worker.postMessage({ rawData });
		worker.onmessage = (e: MessageEvent<TMapTree>) => {
			resolve(e.data);
		};
	});
};

export const getXMLString = (data: TMapTree) => {
	return js2xml(createNestedObjectFromFlatArray(data));
};

export const formatAvailableTimestamps = (data?: IAvailableTimestamps[]): TSelectOption<number>[] => {
	if (!data) return [];

	const { availableTimestamps, configDate: date } = data[0];

	return availableTimestamps?.map(({ availableConfigDate, availableTimestamp }) => {
		return {
			label: availableTimestamp,
			value: availableConfigDate,
			title: date,
		};
	}) || [];
};

export const pickLeftSideDates = (dates: Date[], selectedDate: Date) => {
	if (dates.map(date => formatDate(date)).includes(formatDate(selectedDate))) {
		return selectedDate;
	}

	return dates.filter((date) => date < new Date(selectedDate)).at(-1) || selectedDate;
};

export const renderDayWithTooltip = (configDates: Date[]) => {
	const RenderDayWithTooltip = (day: number, date: Date) => {
		const isDisabled = !configDates.some(configDate => configDate.getTime() === date.getTime());

		return (
			<div>
				<Tooltip
					content='New configuration received'
					isDisabled={isDisabled}
					placement='top'
				>
					<span>{day}</span>
				</Tooltip>
			</div>
		);
	};

	return RenderDayWithTooltip;
};

export const getParentChangedNodes = (data: TMapTree, changedIds: string[]) => {
	const nodeAncestors = changedIds.reduce<string[][]>((acc, id) => {
		const node = data.get(id)!;
		const parent = node.parentPath?.split(FLAT_NODE_PATH_SEPARATOR);

		return parent ? [...acc, parent] : acc;
	}, []);

	const parentNodesIds = nodeAncestors
		.map(parent => parent.at(-1))
		.filter((node): node is string => Boolean(node));

	return {
		parentNodes: parentNodesIds.map(item => data.get(item)!),
		ancestorsIds: new Set(nodeAncestors.flat()),
	};
};

export const getCandidateNodes = ({
	data,
	changedIds,
	parents,
	parentIds = new Set(),
	isWithoutSiblings,
	isWithAlias,
}: {
	data: TMapTree;
	changedIds: string[];
	parents: IFlatXMLTreeNode[];
	parentIds: Set<string>;
	isWithoutSiblings?: boolean;
	isWithAlias?: boolean;
}) => {
	const candidateData: TMapTree = new Map();

	for (const node of data.values()) {
		const isNodeAffected = changedIds.includes(node.id) || parentIds.has(node.id);
		const isNodeWithSiblings = !isWithoutSiblings ? parents.some((parent) =>
			node.path.startsWith(parent.path) &&
			node.depth === parent.depth + 1 &&
			(!node.nodeType || NON_EDITABLE_CONFIGURATION_IDS.has(node.key)),
		) : false;

		const isAlias = isWithAlias && NON_EDITABLE_CONFIGURATION_IDS.has(node.key)
			? Array.from(parentIds).some((parentId) => node.path.endsWith(`${parentId}/${node.id}`))
			: false;

		if (isNodeAffected || isNodeWithSiblings || isAlias) {
			candidateData.set(node.id, node);
		}
	}

	return candidateData;
};

export const getCandidateNodesClosingTags = (candidates: TMapTree, parentIds: Set<string>) => {
	const result = new Map<string, ITagStack[]>();
	const keys = Array.from(candidates.keys());

	for (let index = 0; index < keys.length; index++) {
		const node = candidates.get(keys[index])!;

		const currentChildElement = node.lastChild
			? getLastChild(candidates, node.path, index, keys)
			: null;

		if (currentChildElement) {
			const filteredTags = node.lastChild?.tags.filter((tag) => parentIds.has(tag.id)) || [];

			result.set(currentChildElement.id, result.get(currentChildElement.id)
				? [...result.get(currentChildElement.id)!, ...filteredTags]
				: filteredTags,
			);
		}
	}

	return result;
};

export const filterChangedNodes = ({
	data,
	changedIds,
	isWithoutSiblings = false,
	isWithAlias = true,
}: {
	data: TMapTree;
	changedIds: string[];
	isWithoutSiblings?: boolean;
	isWithAlias?: boolean;
}) => {
	const { parentNodes, ancestorsIds } = getParentChangedNodes(data, changedIds);

	const candidates: TMapTree = getCandidateNodes({
		data,
		changedIds,
		parents: parentNodes,
		parentIds: ancestorsIds,
		isWithoutSiblings,
		isWithAlias,
	});

	const updatedChildTags = getCandidateNodesClosingTags(candidates, ancestorsIds);

	for (const item of Array.from(updatedChildTags.entries())) {
		const [key, value]: [string, ITagStack[]] = item;
		const currentElement = candidates.get(key)!;

		const updatedTags = [...currentElement.tags, ...value]
			.filter((item, index, self) => index === self.findIndex((t) => t.id === item.id))
			.sort((a, b) => (a?.lineNumber ?? 0) - (b?.lineNumber ?? 0));

		candidates.set(key, {
			...currentElement,
			tags: updatedTags,
		});
	}

	return candidates;
};

export const prepareDataForSendingToCandidate = (
	data: TMapTree,
	changedIds: string[],
	isCandidateOnly = false,
): Promise<string[]> => {
	return new Promise((resolve, reject) => {
		try {
			const res = [];

			for (const changedId of changedIds) {
				const filterNode = filterChangedNodes({
					data,
					changedIds: [changedId],
					isWithoutSiblings: isCandidateOnly,
					isWithAlias: true,
				});
				const xmlString = getXMLString(filterNode);

				res.push(xmlString);
			}

			resolve(res);
		} catch (e) {
			reject('Error while preparing data for sending to candidate');
		}
	});
};

interface IColumns {
	openModal: (config: INetworkElementConfiguration | null) => void;
	openChangeMonitoring: (config: INetworkElementConfiguration) => void;
	setIsEditMode: (value: boolean) => void;
	isViewer?: boolean;
}

export const getColumns = ({
	openModal,
	openChangeMonitoring,
	setIsEditMode,
	isViewer,
}: IColumns) => [
	{
		header: 'Name',
		accessorKey: 'networkElementName',
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Type',
		accessorKey: 'type',
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Middleware',
		accessorKey: 'middlewareName',
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: () => <div className={tableStyles.centerColumnHeader}>{`YANG model\ncollection`}</div>,
		accessorKey: 'validationRulesFolderName',
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue() || 'N/A'} />;
		},
	},
	{
		header: 'Status',
		accessorKey: 'status',
		size: columnSizes.size85,
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			const status = getValue();

			return (
				<AdaptiveTableCell
					styleInline={{ color: status?.color }}
					text={status.title}
				/>
			);
		},
	},
	{
		header: 'Last Actual',
		accessorKey: 'storedAtText',
		enableSorting: false,
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: 'Last time checked',
		accessorKey: 'checkedAtText',
		enableSorting: false,
		cell: ({ getValue }: CellContext<INetworkElementConfiguration, any>) => {
			return <AdaptiveTableCell text={getValue()} />;
		},
	},
	{
		header: () => <div className={tableStyles.centerColumnHeader}>Actions</div>,
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size85,
		isHidden: false,
		cell: ({ row: { original } }: CellContext<INetworkElementConfiguration, any>) => {
			const { isConfigUpdatesExists } = original;

			const handleView = () => openModal(original);
			const handleEdit = () => {
				setIsEditMode(true);
				openModal(original);
			};

			const handleMonitoring = () => {
				openChangeMonitoring(original);
			};

			return (
				<div className={tableStyles.actionCell}>
					<Actions
						editHandler={handleEdit}
						gridNumberOfColumns={2}
						isEdit={!isViewer}
						isView
						viewHandler={handleView}
					/>
					{isConfigUpdatesExists && (
						<Button
							className={tableStyles.actionButton}
							onClick={handleMonitoring}
							secondary
							text='View changes'
						/>
					)}
				</div>
			);
		},
	},
];
